<template>
    <sankey-v2
        :data="chartData"
        :types="chartTypes"
        
        :is-loading="isLoading"

        :highlight-mode="highlightMode"
        :selected-brick-id.sync="selectedBrickId"
        :tag-route-type="tagRouteType"
    >
        <touch-point-info v-if="displayTouchpointInfo"
                          ref="touchpointInfo"
                          class="touchpoint-info"
                          @close="selectedBrickId = null"
        />
    </sankey-v2>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';

const {mapState, mapGetters, mapMutations, mapActions} = createNamespacedHelpers('journeyBuilder/reporting');

import {supportsStatistics} from "@modules/JourneyBuilder/Components/Charts/SankeyV2/helpers";
import {bootstrapDangerGrowl as errorGrowl} from "@core/Util/growler";
import $ from "jquery";

import TouchPointInfo from "./TouchpointInfo";
import SankeyV2 from "@modules/JourneyBuilder/Components/Charts/SankeyV2";
import LoadingBox from '@core/Components/Util/LoadingBox';

export default {
    name: "journey-statistics-sankey",
    
    components: {
        TouchPointInfo,
        SankeyV2,
        LoadingBox
    },
    
    data: () => ({
        isLoading: false
    }),
    
    computed: {
        ...mapGetters({
            'watchableReportStatisticsParameters': 'watchableReportStatisticsParameters',
            'selectedNodes': 'selectedNodes',
            'canLoadSankeyData': 'canLoadSankeyData', 
            'chartData': 'activeSankeyData'
        }),
        ...mapState({
            'journeyId': 'journeyId',
            'storedSelectedBrickId': 'selectedBrickId',
            'chartTypes': 'sankeyTypes',
            'highlightMode': 'highlightMode',
            'tagRouteType': 'tagRouteType'
        }),
        selectedBrickId: {
            get(){
                return this.storedSelectedBrickId;
            },
            set(value){
                this.setSelectedBrickId(value);
            }
        },
        displayTouchpointInfo(){
            if(this.selectedNodes.length){
                //All node types are expected to be the same 
                return supportsStatistics(this.selectedNodes[0]);
            }

            return false;
        }
    },
    
    watch: {
        watchableReportStatisticsParameters(){
            this.drawChartFromRemoteData();
        },
        
        journeyId(){
            this.selectedBrickId = undefined;
        },
        
        async displayTouchpointInfo(value){
            //Not unsticking our sticky element prior to deletion causes errors upon re-stickying a new element
            if(!value){
                $(this.$refs.touchpointInfo.$el).unstick();
                return;
            }

            //On addition to the DOM - Make element sticky, scroll along with user
            await this.$nextTick();

            const headerHeight = document.querySelector('.main-header')
                .getBoundingClientRect()
                .height;

            $(this.$refs.touchpointInfo.$el).sticky({
                topSpacing: headerHeight,
                widthFromWrapper: false
            }).sticky('update');
        }
    },

    async mounted() {
        await this.drawChartFromRemoteData();
    },

    methods: {
        ...mapMutations(['setSankeyData', 'setSelectedBrickId']),
        ...mapActions(['loadSankeyData']),
        async drawChartFromRemoteData(){
            this.isLoading = true;

            //Simply display a loader while we wait for external required parameters to load
            if(!this.canLoadSankeyData) {
                return;
            }
            
            try {
                await this.loadSankeyData();
            } catch(e){
                console.error(e);
                errorGrowl(e);
            } finally {
                this.isLoading = false;
            }
        },
    },
}
</script>

<style scoped lang="scss">
    .touchpoint-info {
        position: absolute;
        padding-right: 30px;
        right: 0;
        min-width: 700px;
        width: 50%;
        height: 600px;
    
        //Ensure our element is rendered on top of our goal containers
        z-index: 1 !important; //Important to overwrite sticky plugin DOM manipulation
    }
    
    //Sticky alters positioning/width calculation slightly - alter CSS to not display any difference in width
    .is-sticky .touchpoint-info {
        right: 39px;
        padding-right: 1px;
    }
</style>

<style scoped lang="scss">
    @import "~@modules/JourneyBuilder/Components/Charts/SankeyV2/chart-highlighting.scss";
    
    @include enable-highlight;
    @include enable-node-hover-highlight;
    @include enable-link-hover-highlight;
</style>